import { useEffect, useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Grid,
  LinearProgress,
  Avatar,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import { 
  AccountBalanceWallet as BudgetIcon,
  TrendingUp as UsedIcon,
  AccountBalance as RemainingIcon,
  Storage as ServiceIcon
} from '@mui/icons-material';
import { useParams } from "react-router-dom";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import InlineMessage from "components/InlineMessage";

export const BudgetIndexPage = () => {
  const [budgetData, setBudgetData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { projectId } = useParams();

  useEffect(() => {
    const fetchBudgetData = async () => {
      if (!projectId) {
        setError('Project ID is required to load budget information.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`/budget/budget/?project_id=${projectId}`);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setBudgetData(data);
      } catch (error) {
        console.error('Error fetching budget data:', error);
        setError(error.message || 'Failed to load budget information. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBudgetData();
  }, [projectId]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatLastUpdated = (lastUpdated) => {
    if (!lastUpdated) return null;

    const date = new Date(lastUpdated);
    return `Usage data last updated at ${date.toLocaleString()}`;
  };

  const getUsagePercentage = () => {
    if (!budgetData || budgetData.budget === 0) return 0;
    return Math.round((budgetData.usage / budgetData.budget) * 100);
  };

  const isOverBudget = () => {
    return budgetData && budgetData.usage > budgetData.budget;
  };

  const getRemainingBudgetDisplay = () => {
    if (!budgetData) return { amount: 0, color: '#2e7d32', subtitle: '' };

    if (budgetData.remaining >= 0) {
      return {
        amount: budgetData.remaining,
        color: '#2e7d32'
      };
    } else {
      return {
        amount: Math.abs(budgetData.remaining),
        color: '#d32f2f'
      };
    }
  };

  const getUsageDisplay = () => {
    if (!budgetData) return { percentage: 0, color: '#ed6c02', subtitle: '' };

    const percentage = getUsagePercentage();
    if (percentage <= 100) {
      return {
        percentage,
        color: '#ed6c02',
        subtitle: `${percentage}% of total monthly budget`
      };
    } else {
      return {
        percentage: 100,
        color: '#d32f2f',
        subtitle: `${percentage}% of total monthly budget`
      };
    }
  };

  const getDaysLeftInMonth = () => {
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const daysLeft = lastDayOfMonth.getDate() - now.getDate();
    return daysLeft;
  };

  const getCurrentMonthInfo = () => {
    const now = new Date();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[now.getMonth()];
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `${monthName} (${daysInMonth} days)`;
  };

  const BudgetCard = ({ title, amount, subtitle, icon, color, progressValue, progressColor, showProgress = true }) => (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.3s ease-in-out',
        border: `1px solid ${color}20`,
        background: 'white'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar 
            sx={{ 
              bgcolor: `${color}20`, 
              color: color,
              width: 48,
              height: 48,
              mr: 2
            }}
          >
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h4" color="text.secondary" gutterBottom>
              {title}
            </Typography>
          </Box>
        </Box>
        
        <Typography 
          variant="h3" 
          component="div" 
          sx={{ 
            fontWeight: 700,
            color: color,
            mb: 1
          }}
        >
          {amount}
        </Typography>
        
        {subtitle && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {subtitle}
          </Typography>
        )}
        
        {showProgress && (
          <Box sx={{ mt: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" fontWeight="bold" color={color}>
                {progressValue}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(progressValue, 100)} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: `${color}20`,
                '& .MuiLinearProgress-bar': {
                  bgcolor: progressColor || color,
                  borderRadius: 4,
                }
              }} 
            />
            {progressValue > 100 && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                ⚠️ Budget exceeded by {progressValue - 100}%
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const CostsByServiceTable = () => {
    const costsByService = budgetData?.costs_by_service || [];

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Avatar
              sx={{
                bgcolor: '#1976d220',
                color: '#1976d2',
                width: 40,
                height: 40,
                mr: 2
              }}
            >
              <ServiceIcon />
            </Avatar>
            <Typography variant="h4" color="text.primary">
              Costs by Service
            </Typography>
          </Box>

          {costsByService.length === 0 ? (
            <InlineMessage
              type="info"
              message="No cost data available for this project."
              sxOverrides={{ mb: 2 }}
            />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '60%' }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%' }} align="right">Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="right">Percentage of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costsByService.map((service, index) => {
                    const serviceCost = parseFloat(service.cost || 0);
                    const percentage = budgetData && budgetData.usage > 0
                      ? Math.round((serviceCost / budgetData.usage) * 100)
                      : 0;

                    return (
                      <TableRow key={index} hover>
                        <TableCell sx={{ width: '60%' }}>
                          <Typography variant="body2">
                            {service.service_name || 'Unknown Service'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: '20%' }} align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {formatCurrency(serviceCost)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: '10%' }} align="right">
                          <Typography variant="body2" color="text.secondary">
                            {percentage}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {costsByService.length > 0 && (
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ width: '60%' }}>
                        <Typography variant="body2" fontWeight="bold">
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '25%' }} align="right">
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatCurrency(costsByService.reduce((sum, service) => sum + parseFloat(service.cost || 0), 0))}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '15%' }} align="right">
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          100%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <PageHeader
        title="Budget"
        subtitle="Monitor your project's monthly budget and usage."
      />
      
      <Box component="section">
        <InlineMessage
          type="info"
          message="Note: This is an estimate of your usage based on exports from Google Cloud Platform and should only be used directionally. Usage data can be delayed by up to 24 hours. Your actual usage and final billing will be reflected in your monthly invoice."
          sxOverrides={{ mb: 2 }}
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <InlineMessage
            type="error"
            message={error}
            sxOverrides={{ mb: 2 }}
          />
        ) : budgetData ? (
          <Fade in={true} timeout={800}>
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <BudgetCard
                    title="Total Monthly Budget"
                    amount={`${formatCurrency(budgetData.budget)}`}
                    subtitle={`Current month: ${getCurrentMonthInfo()}`}
                    icon={<BudgetIcon />}
                    color="#1976d2"
                    showProgress={false}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <BudgetCard
                    title="Month-to-Date (MTD) Costs"
                    amount={formatCurrency(budgetData.usage)}
                    subtitle={getUsageDisplay().subtitle}
                    icon={<UsedIcon />}
                    color={getUsageDisplay().color}
                    progressValue={getUsageDisplay().percentage}
                    progressColor={getUsageDisplay().color}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <BudgetCard
                    title={isOverBudget() ? "Budget Overrun" : "Remaining Budget"}
                    amount={isOverBudget() ? `+${formatCurrency(getRemainingBudgetDisplay().amount)}` : formatCurrency(getRemainingBudgetDisplay().amount)}
                    subtitle={`${getDaysLeftInMonth()} days left this month`}
                    icon={<RemainingIcon />}
                    color={getRemainingBudgetDisplay().color}
                    showProgress={false}
                  />
                </Grid>
              </Grid>

              <CostsByServiceTable />

              {budgetData?.last_updated && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <InlineMessage
                    type="success"
                    message={formatLastUpdated(budgetData.last_updated)}
                  />
                </Box>
              )}
            </Box>
          </Fade>
        ) : (
          <InlineMessage
            type="info"
            message="No budget information available for this project."
            sxOverrides={{ mb: 2 }}
          />
        )}
      </Box>
    </>
  );
}

const routeArgs = {
    element: <BudgetIndexPage />,
    loader: async () => {
        return { budget: [] };
    },
};

export default buildRoute(routeArgs);